import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import ReactPlayerLoader from '@brightcove/react-player-loader';
import { MediaPlayerError } from '@yi/core';

import {
  BRIGHCOVE_ACCOUNT_ID,
  BRIGHCOVE_PLAYER_ID,
  BRIGHCOVE_TEST_PLAYER_ID,
} from '~/src/common/media';
// we need to import from the more deeper module for the sake of testability
import { isNonEmptyString } from '~/src/common/utilities';
import { useFeatureFlag } from '~/src/components/hooks/useFeatureFlag';
import {
  CommonYouboraAnalytics,
  YouboraAnaliticsProvider,
  initializeYouboraPlugin,
} from '~/src/components/MediaPlayer/Youbora';

import type NpawPlugin from 'npaw-plugin';

import { initializeVideoJSConsumedSecondsPlugin } from './initializeVideoJSConsumedSecondsPlugin';
import { isExtendedPlayer } from './isExtendedPlayer';
import { MediaSources } from './MediaSources';
import { Thumbnails } from './Thumbnails';
import { useLogging, useReportMediaPlaybackError } from '../../logger';
import { useMediaPlayer } from '../../MediaPlayerProvider';

type ReactPlayerLoaderProps = React.ComponentProps<typeof ReactPlayerLoader>;

function getPlayerId(useTestPlayer: boolean): string {
  return useTestPlayer ? BRIGHCOVE_TEST_PLAYER_ID : BRIGHCOVE_PLAYER_ID;
}

export const PlayerLoader: React.FC<PropsWithChildren> = ({ children }) => {
  useLogging();
  const report = useReportMediaPlaybackError();

  const { setPlayer } = useMediaPlayer();

  const [youboraPlugin, setYouboraPlugin] = useState<null | NpawPlugin>(null);

  const { enabled: useTestPlayer, loading } = useFeatureFlag('brightcove_test_player');

  const onFailure: Required<ReactPlayerLoaderProps>['onFailure'] = useCallback(
    err => {
      report(err);
    },
    [report],
  );
  const onSuccess: Required<ReactPlayerLoaderProps>['onSuccess'] = useCallback(
    success => {
      const newPlayerInstance = success.ref;
      initializeVideoJSConsumedSecondsPlugin(newPlayerInstance);
      const initializedYouboraPlugin = initializeYouboraPlugin(newPlayerInstance);
      setYouboraPlugin(initializedYouboraPlugin);

      if (!isExtendedPlayer(newPlayerInstance)) {
        report(
          new MediaPlayerError(
            `media player isn't extented. Some features will not work or work unreliable. (for ex. analytics event)`,
          ),
        );
        return;
      }
      newPlayerInstance.ready(() => {
        setPlayer(newPlayerInstance);
      });
    },
    [report, setPlayer],
  );

  const playerId = getPlayerId(useTestPlayer);
  const mediaPlayerProps: ReactPlayerLoaderProps = useMemo(() => {
    return {
      playerId,
      accountId: BRIGHCOVE_ACCOUNT_ID,
      onFailure,
      onSuccess,
    };
  }, [onFailure, onSuccess, playerId]);

  if (loading) {
    return null;
  }

  if (!isNonEmptyString(mediaPlayerProps.accountId)) {
    report(new MediaPlayerError(`Brightcove account id is not specified`));
    return null;
  }

  if (!isNonEmptyString(mediaPlayerProps.playerId)) {
    report(new MediaPlayerError(`Brightcove player id is not specified`));
    return null;
  }

  return (
    <>
      <ReactPlayerLoader {...mediaPlayerProps} />
      <MediaSources />
      <Thumbnails />
      <YouboraAnaliticsProvider plugin={youboraPlugin}>
        <CommonYouboraAnalytics />
        {children}
      </YouboraAnaliticsProvider>
    </>
  );
};
